const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Parse YYYY-MM-DD to a UTC timestamp at 00:00:00.000Z.
 * @param {string} ymd
 * @returns {number}
 */
export function parseYmdToUtcMs(ymd) {
  if (!ymd || typeof ymd !== 'string') return NaN;
  const [y, m, d] = ymd.split('-').map((x) => Number(x));
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d, 0, 0, 0, 0);
}

/**
 * Compute a time-based trailing-window average over a date-indexed series.
 * For each i, includes points with date >= dates[i] - windowDays.
 *
 * Assumes dates are sorted ascending and correspond 1:1 with values.
 *
 * @param {Object} params
 * @param {string[]} params.dates - YYYY-MM-DD
 * @param {number[]} params.values
 * @param {number} params.windowDays - e.g. 7, 30, 365
 * @param {boolean} [params.skipNulls=false] - when true, null/NaN values are excluded from avg
 * @returns {number[]}
 */
export function computeTrailingWindowAverage({
  dates = [],
  values = [],
  windowDays,
  skipNulls = false,
}: any) {
  const n = Math.min(dates.length, values.length);
  if (n === 0) return [];

  const wDays = Number(windowDays);
  const windowMs = Number.isFinite(wDays) && wDays > 0 ? wDays * DAY_MS : 0;

  const out = new Array(n).fill(0);

  let start = 0;
  let sum = 0;
  let count = 0;

  const ts = new Array(n);
  for (let i = 0; i < n; i++) ts[i] = parseYmdToUtcMs(dates[i]);

  const norm = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? num : null;
  };

  for (let i = 0; i < n; i++) {
    const vi = norm(values[i]);
    if (vi != null) {
      sum += vi;
      count += 1;
    } else if (!skipNulls) {
      // Treat missing as 0
      count += 1;
    }

    const cutoff = ts[i] - windowMs;
    while (start <= i && ts[start] < cutoff) {
      const vs = norm(values[start]);
      if (vs != null) {
        sum -= vs;
        count -= 1;
      } else if (!skipNulls) {
        count -= 1;
      }
      start += 1;
    }

    out[i] = count > 0 ? sum / count : 0;
  }

  return out;
}
